/**
 * DeliveryNotificationCenter Component
 * Shows delivery notifications and updates to users
 */

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Bell,
  Package,
  Truck,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Eye,
  EyeOff,
  X,
  Settings
} from 'lucide-react'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { Modal } from '../ui/Modal'
import { useDeliveryNotifications } from '../../services/deliveryNotificationService'
import { useAuthStore } from '../../stores/authStore'
import type { DeliveryNotification } from '../../types/prizeDelivery'

interface DeliveryNotificationCenterProps {
  className?: string
}

const notificationIcons = {
  'status_change': Clock,
  'shipped': Truck,
  'delivered': CheckCircle,
  'delayed': AlertCircle,
  'cancelled': XCircle
}

const notificationColors = {
  'status_change': 'text-blue-600',
  'shipped': 'text-orange-600',
  'delivered': 'text-green-600',
  'delayed': 'text-amber-600',
  'cancelled': 'text-red-600'
}

export const DeliveryNotificationCenter: React.FC<DeliveryNotificationCenterProps> = ({
  className = ''
}) => {
  const { user } = useAuthStore()
  const { notifications, unreadCount, hasUnread } = useDeliveryNotifications(user?.id)

  const [isOpen, setIsOpen] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [filter, setFilter] = useState<'all' | 'unread'>('all')

  const formatNotificationTime = (date: Date) => {
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))

    if (diffInMinutes < 1) return 'Ahora mismo'
    if (diffInMinutes < 60) return `Hace ${diffInMinutes} min`

    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) return `Hace ${diffInHours}h`

    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) return `Hace ${diffInDays}d`

    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short'
    })
  }

  const getNotificationMessage = (notification: DeliveryNotification) => {
    switch (notification.type) {
      case 'shipped':
        return 'Tu premio ha sido enviado y está en camino'
      case 'delivered':
        return 'Tu premio ha sido entregado con éxito'
      case 'cancelled':
        return 'La entrega de tu premio ha sido cancelada'
      case 'delayed':
        return 'Tu entrega se ha retrasado'
      case 'status_change':
        return 'Hay una actualización en el estado de tu entrega'
      default:
        return 'Actualización de entrega'
    }
  }

  const filteredNotifications = React.useMemo(() => {
    let filtered = notifications

    if (filter === 'unread') {
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000)
      filtered = filtered.filter(n =>
        n.createdAt > oneDayAgo && n.status === 'sent'
      )
    }

    return filtered.slice(0, 20) // Show last 20 notifications
  }, [notifications, filter])

  const groupedNotifications = React.useMemo(() => {
    const groups: { [key: string]: DeliveryNotification[] } = {}

    filteredNotifications.forEach(notification => {
      const date = notification.createdAt.toDateString()
      if (!groups[date]) {
        groups[date] = []
      }
      groups[date].push(notification)
    })

    return groups
  }, [filteredNotifications])

  const getDateLabel = (dateString: string) => {
    const date = new Date(dateString)
    const today = new Date().toDateString()
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toDateString()

    if (dateString === today) return 'Hoy'
    if (dateString === yesterday) return 'Ayer'

    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'long'
    })
  }

  return (
    <>
      {/* Notification Bell Button */}
      <div className={`relative ${className}`}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsOpen(true)}
          className="relative p-2 hover:bg-gray-100 rounded-full"
          aria-label={`Notificaciones${hasUnread ? ` (${unreadCount} sin leer)` : ''}`}
        >
          <Bell className="w-5 h-5 text-gray-600" />

          {hasUnread && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
            >
              <span className="text-xs font-medium text-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            </motion.div>
          )}
        </Button>
      </div>

      {/* Notification Modal */}
      <AnimatePresence>
        {isOpen && (
          <Modal
            isOpen={true}
            onClose={() => setIsOpen(false)}
            title="Notificaciones de entregas"
            size="md"
          >
            <div className="max-h-96 overflow-hidden">
              {/* Header */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <h3 className="font-semibold text-gray-900">
                      Notificaciones
                    </h3>
                    {hasUnread && (
                      <Badge variant="primary" className="text-xs">
                        {unreadCount} nuevas
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowSettings(true)}
                    className="p-1"
                    aria-label="Configurar notificaciones"
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                </div>

                {/* Filters */}
                <div className="flex space-x-2">
                  <Button
                    variant={filter === 'all' ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => setFilter('all')}
                  >
                    Todas
                  </Button>
                  <Button
                    variant={filter === 'unread' ? 'primary' : 'ghost'}
                    size="sm"
                    onClick={() => setFilter('unread')}
                  >
                    Sin leer
                  </Button>
                </div>
              </div>

              {/* Notifications List */}
              <div className="max-h-80 overflow-y-auto">
                {Object.keys(groupedNotifications).length === 0 ? (
                  <div className="text-center py-8">
                    <Bell className="w-12 h-12 mx-auto mb-3 text-gray-400" aria-hidden="true" />
                    <p className="text-gray-600 text-sm">
                      {filter === 'unread' ? 'No hay notificaciones sin leer' : 'No hay notificaciones'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(groupedNotifications).map(([dateString, dayNotifications]) => (
                      <div key={dateString}>
                        {/* Date Header */}
                        <div className="sticky top-0 bg-gray-50 px-4 py-2 border-b border-gray-100">
                          <h4 className="text-sm font-medium text-gray-700">
                            {getDateLabel(dateString)}
                          </h4>
                        </div>

                        {/* Day's Notifications */}
                        <div className="space-y-2 px-4 py-2">
                          {dayNotifications.map((notification) => {
                            const Icon = notificationIcons[notification.type]
                            const iconColor = notificationColors[notification.type]
                            const isUnread = notification.createdAt > new Date(Date.now() - 24 * 60 * 60 * 1000) && notification.status === 'sent'

                            return (
                              <motion.div
                                key={notification.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className={`
                                  flex items-start space-x-3 p-3 rounded-lg transition-colors
                                  ${isUnread ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'}
                                `}
                              >
                                {/* Icon */}
                                <div className={`flex-shrink-0 p-2 rounded-full bg-white shadow-sm`}>
                                  <Icon className={`w-4 h-4 ${iconColor}`} aria-hidden="true" />
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-900 leading-relaxed">
                                    {getNotificationMessage(notification)}
                                  </p>
                                  <div className="flex items-center justify-between mt-1">
                                    <span className="text-xs text-gray-500">
                                      {formatNotificationTime(notification.createdAt)}
                                    </span>
                                    <div className="flex items-center space-x-2">
                                      {notification.method === 'email' && (
                                        <span className="text-xs text-gray-400">Email</span>
                                      )}
                                      {notification.method === 'push' && (
                                        <span className="text-xs text-gray-400">Push</span>
                                      )}
                                      {isUnread && (
                                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-gray-200">
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>
                    {filteredNotifications.length} notificacion{filteredNotifications.length !== 1 ? 'es' : ''}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsOpen(false)}
                  >
                    Cerrar
                  </Button>
                </div>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <Modal
            isOpen={true}
            onClose={() => setShowSettings(false)}
            title="Configuración de notificaciones"
          >
            <div className="p-6 space-y-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-4">
                  Métodos de notificación
                </h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="font-medium text-gray-900">Email</h5>
                      <p className="text-sm text-gray-600">
                        Recibe emails cuando cambie el estado de tus entregas
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      defaultChecked={user?.preferences?.notifications?.email}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="font-medium text-gray-900">Push</h5>
                      <p className="text-sm text-gray-600">
                        Recibe notificaciones push en tiempo real
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      defaultChecked={user?.preferences?.notifications?.push}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="font-medium text-gray-900">SMS</h5>
                      <p className="text-sm text-gray-600">
                        Recibe SMS para actualizaciones críticas
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      defaultChecked={user?.preferences?.notifications?.sms}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-4">
                  Tipos de notificación
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Cambios de estado</span>
                    <input
                      type="checkbox"
                      defaultChecked
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Envíos realizados</span>
                    <input
                      type="checkbox"
                      defaultChecked
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Entregas completadas</span>
                    <input
                      type="checkbox"
                      defaultChecked
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Retrasos y problemas</span>
                    <input
                      type="checkbox"
                      defaultChecked
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowSettings(false)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={() => setShowSettings(false)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Guardar configuración
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </>
  )
}

/**
 * Compact version for header/navbar
 */
export const DeliveryNotificationBell: React.FC<{
  className?: string
  onClick?: () => void
}> = ({ className = '', onClick }) => {
  const { user } = useAuthStore()
  const { unreadCount, hasUnread } = useDeliveryNotifications(user?.id)

  return (
    <button
      onClick={onClick}
      className={`relative p-2 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`}
      aria-label={`Notificaciones de entregas${hasUnread ? ` (${unreadCount} sin leer)` : ''}`}
    >
      <Bell className="w-5 h-5 text-gray-600" />

      {hasUnread && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center"
        >
          <span className="text-xs font-medium text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        </motion.div>
      )}
    </button>
  )
}